/**
 * Azure Speech Service Module
 *
 * High-level TypeScript API for pronunciation assessment.
 * This wraps the native TurboModule and provides:
 * - Type-safe API
 * - Event subscriptions
 * - Error handling
 * - Token management
 *
 * Usage Example:
 * ```typescript
 * import { AzureSpeech } from './modules/AzureSpeech';
 *
 * // Get the relay connection (host + Supabase JWT) from the backend
 * const { host, token } = await getSpeechRelayConnection();
 *
 * // Subscribe to real-time partial transcription
 * const subscription = AzureSpeech.subscribeToEvents({
 *   onRecognizing: (text) => console.log('Recognizing:', text),
 * });
 *
 * // Start assessment
 * const result = await AzureSpeech.assessPronunciation({
 *   host,
 *   token,
 *   referenceText: "Hello, how are you?",
 *   language: "en-US",
 * });
 *
 * // Cleanup
 * subscription.remove();
 * ```
 */

import { Platform } from 'react-native';
import NativeAzureSpeech from './NativeAzureSpeech';
import type {
  AssessmentConfig,
  PronunciationResult,
  RecognitionEvent,
} from './NativeAzureSpeech';
import { speechEventEmitter } from './speechEventEmitter';
import { webEventEmitter } from './webEventEmitter';
import { waitForRecognizerIdle } from './recognizerIdle';
import { consumeInjectedAudio } from './e2eAudioInjection';
import { ASSESSMENT_DEFAULTS, SCRIPTED_INITIAL_SILENCE_MS } from './speechDefaults';
import { selectProfile, getProfileConfig } from './assessmentProfiles';
import type { PronunciationFeatures } from './assessmentProfiles';

import { createLogger } from '../utils/logger';
import { SpeechLogger } from './AzureSpeechLogger';

// Logger instance — logRN flag is set when startAssessment is called
let slog = new SpeechLogger(createLogger('AzureSpeech'), false, false);

// Event emitter: shared native emitter on iOS/Android, shared web emitter on web
const eventEmitter = (Platform.OS === 'web' ? webEventEmitter : speechEventEmitter)!;

/**
 * Parameters for pronunciation assessment
 */
export interface AssessmentParams {
  host: string;                     // Relay WS origin for SpeechConfig.fromHost() (server-speech-proxy Phase 2)
  token: string;                    // Supabase session JWT — the SDK's authorizationToken (Bearer on the WS upgrade)
  referenceText?: string;           // What the user should say (empty for unscripted assessment)
  language: string;                 // BCP 47 tag (e.g., "en-US")
  phraseList?: string[];            // Words/phrases to boost recognition (e.g., app name, user name)
  pronunciationFeatures?: PronunciationFeatures | null; // Per-language feature flags from locale data
  /** Checked after the recognizer-idle wait, right before the native session
   *  starts — a cancel landing during that wait must not spawn a session
   *  nobody owns. Return true to abort with a userCanceled empty result. */
  shouldAbort?: () => boolean;
}

/**
 * Event subscription callbacks
 */
export interface EventCallbacks {
  onRecognizing?: (text: string) => void;       // Partial recognition (real-time)
}

/**
 * Event subscription handle
 */
export interface EventSubscription {
  remove: () => void;
}

/**
 * Cancellable assessment handle
 * Promise resolves to null if cancelled, otherwise the result.
 */
export interface CancellableAssessment {
  promise: Promise<PronunciationResult | null>;
  /** Discard the take and abort recognition — promise resolves to null. */
  cancel: () => void;
  /** Keep the take: end recording and let the assessment complete — the
   *  promise resolves with the result (or a userCanceled empty result when
   *  nothing was recognized yet). */
  stop: () => void;
}

/**
 * Azure Speech Service API
 */
class AzureSpeechModule {
  private listeners: { remove: () => void }[] = [];

  constructor() {
    // Permanent listeners for native events — routes to JS logger/Sentry.
    // onWarning/onSDKLog only exist on the native module (null on web).
    speechEventEmitter?.addListener('onWarning', (event: { message: string }) => {
      slog.rnAnomaly('NativeSDKWarning', { warning: event.message });
    });

    // Route native SDK anomalies through JS logger → Metro + Sentry
    speechEventEmitter?.addListener(
      'onSDKLog',
      (event: { tag: string; level: string; detail: string }) => {
        const detail = event.detail ? { detail: event.detail } : undefined;
        slog.sdkAnomaly(event.tag, detail);
      },
    );
  }

  /**
   * Start pronunciation assessment
   *
   * @param params - Assessment parameters
   * @returns Promise<PronunciationResult>
   * @throws Error if assessment fails
   */
  async assessPronunciation(params: AssessmentParams): Promise<PronunciationResult> {
    try {
      // A just-cancelled session may still be tearing down — wait for the
      // recognizer to go idle so an immediate retry doesn't reject with
      // already_recognizing.
      await waitForRecognizerIdle();

      // Select profile from reference text length + language features
      const profile = selectProfile(params.referenceText);

      if (params.shouldAbort?.()) {
        return {
          recognizedText: '',
          displayText: '',
          accuracyScore: 0,
          fluencyScore: 0,
          completenessScore: 0,
          pronunciationScore: 0,
          words: [],
          duration: 0,
          userCanceled: true,
        };
      }
      const profileConfig = getProfileConfig(profile, params.pronunciationFeatures);

      // textForm defaults to 'lexical' in the native modules — words-as-spoken.
      // Override via config.textForm for display/ITN/maskedItn on other call sites.
      const config: AssessmentConfig = {
        ...ASSESSMENT_DEFAULTS,
        ...profileConfig,
        referenceText: params.referenceText || '',
        language: params.language,
        endOfTurnSilenceMs: 3000,
        // Scripted: give the user time to read the reference before speaking
        ...(params.referenceText?.trim()
          ? { initialSilenceTimeoutMs: SCRIPTED_INITIAL_SILENCE_MS }
          : {}),
        phraseList: params.phraseList,
      };

      // E2E audio injection: if a clip is armed, the recognizer reads it instead
      // of the microphone. Consume-once and E2E_MODE-gated, so this is a no-op on
      // every production recording — see e2eAudioInjection.ts for the three
      // independent gates.
      const injected = consumeInjectedAudio();
      if (injected) {
        config.injectedAudioBase64 = injected.wavBase64;
      }

      // Update logger flags for this session
      slog = new SpeechLogger(
        createLogger('AzureSpeech'),
        config.logRN ?? __DEV__,
        config.logSDK ?? __DEV__,
      );

      // Logged after the logger is rebuilt with this session's flags — logging
      // before would silently drop the line on the previous session's flags.
      if (injected) {
        slog.rnEvent('AudioInjected', 'start', {
          clipId: injected.clipId,
          sha256: injected.sha256,
        });
      }

      // All profiles run on the continuous recognizer — it has a safe, prompt
      // stop/cancel and fully supports scripted assessment (single-segment takes
      // short-circuit through mergeSegments).
      const result = await NativeAzureSpeech.startPronunciationAssessment(
        params.host,
        params.token,
        config,
      );

      // Attach profile so downstream consumers know the assessment scenario
      return { ...result, profile };
    } catch (error: unknown) {
      const nativeCode = (error as { code?: string })?.code;
      slog.rnAnomaly('AssessmentFailed', { code: nativeCode, error });
      throw error instanceof Error ? error : new Error('Pronunciation assessment failed');
    }
  }

  /**
   * Stop ongoing recognition (graceful — the take is kept).
   * Safe to call even if no recognition is in progress
   */
  stopRecognition(): void {
    try {
      NativeAzureSpeech.stopRecognition();
    } catch (error: unknown) {
      slog.rnAnomaly('StopRecognitionFailed', error);
    }
  }

  /**
   * Force-cancel ongoing recognition (the take is discarded).
   * Stops the continuous session and releases the microphone. Safe to call
   * even if no recognition is in progress.
   */
  cancelRecognition(): void {
    try {
      NativeAzureSpeech.cancelRecognition();
    } catch (error: unknown) {
      slog.rnAnomaly('CancelRecognitionFailed', error);
      // Native builds predating cancelRecognition: fall back to stop.
      this.stopRecognition();
    }
  }

  /**
   * Check if recognition is currently in progress
   *
   * @returns boolean
   */
  isRecognizing(): boolean {
    try {
      return NativeAzureSpeech.isRecognizing();
    } catch (_error) {
      return false;
    }
  }

  /**
   * Subscribe to real-time recognition events
   *
   * @param callbacks - Event callbacks
   * @returns EventSubscription handle (call .remove() to unsubscribe)
   */
  subscribeToEvents(callbacks: EventCallbacks): EventSubscription {
    const listeners: { remove: () => void }[] = [];

    // Recognizing event (partial, real-time transcription while promise is pending)
    if (callbacks.onRecognizing) {
      const listener = eventEmitter.addListener(
        'onRecognizing',
        (event: RecognitionEvent) => {
          if (event.text && callbacks.onRecognizing) {
            callbacks.onRecognizing(event.text);
          }
        }
      );
      listeners.push(listener);
    }

    // Store listeners for cleanup
    this.listeners.push(...listeners);

    // Return subscription handle
    return {
      remove: () => {
        listeners.forEach((listener) => listener.remove());
        this.listeners = this.listeners.filter((l) => !listeners.includes(l));
      },
    };
  }

  /**
   * Start pronunciation assessment with cancel support.
   * Returns a handle whose promise resolves to null if cancelled.
   */
  assessPronunciationWithCancel(params: AssessmentParams): CancellableAssessment {
    let cancelled = false;

    const promise = this.assessPronunciation({
      ...params,
      // Cancel during the recognizer-idle wait must not start the session.
      shouldAbort: () => cancelled || (params.shouldAbort?.() ?? false),
    }).then(
      (result) => (cancelled ? null : result),
      (error) => {
        if (cancelled) return null;
        throw error;
      }
    );

    return {
      promise,
      cancel: () => {
        cancelled = true;
        this.cancelRecognition();
      },
      stop: () => {
        this.stopRecognition();
      },
    };
  }

  /**
   * Remove all event listeners
   * Call this when your component unmounts
   */
  removeAllListeners(): void {
    this.listeners.forEach((listener) => listener.remove());
    this.listeners = [];
  }

  /**
   * Get score color based on value
   * Used for UI visualization
   *
   * @param score - Score value (0-100)
   * @returns Color string
   */
  getScoreColor(score: number): string {
    if (score >= 80) return '#27AE60'; // Green - Excellent
    if (score >= 60) return '#F39C12'; // Orange - Good
    if (score >= 40) return '#E67E22'; // Dark orange - Fair
    return '#E74C3C'; // Red - Needs improvement
  }

  /**
   * Get score label based on value
   *
   * @param score - Score value (0-100)
   * @returns Label string
   */
  getScoreLabel(score: number): string {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  }
}

/**
 * Singleton instance
 * Export as default for easy importing
 */
export const AzureSpeech = new AzureSpeechModule();

/**
 * Re-export types for convenience
 */
export type {
  AssessmentConfig,
  PronunciationResult,
  PronunciationWord,
  PronunciationPhoneme,
  RecognitionEvent,
} from './NativeAzureSpeech';

export type {
  AssessmentProfile,
  PronunciationFeatures,
} from './assessmentProfiles';

export {
  selectProfile,
  getProfileConfig,
} from './assessmentProfiles';
